import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Send, X } from 'lucide-react';
import { releaseProjectFileToVendor } from '../../utils/vendorFileReleaseService';
import { compareVersionsNewestFirst } from '../../utils/projectFileStreamVersions';
import type { ProjectFile } from '../../types';

export interface ReleaseToVendorModalProps {
  rfqProjectId: string;
  supplierOrgId: string;
  dealId: string;
  streamTitle: string;
  versions: ProjectFile[];
  initialVersionId?: string | null;
  onClose: () => void;
  onReleased?: (result: { releaseId: string; releasedUrl: string; name: string }) => void;
}

const ReleaseToVendorModal: React.FC<ReleaseToVendorModalProps> = ({
  rfqProjectId,
  supplierOrgId,
  dealId,
  streamTitle,
  versions,
  initialVersionId,
  onClose,
  onReleased,
}) => {
  const sortedVersions = useMemo(
    () => [...versions].sort(compareVersionsNewestFirst),
    [versions],
  );

  const [selectedFileId, setSelectedFileId] = useState<string>(() => {
    if (initialVersionId) return initialVersionId;
    return sortedVersions[0]?.id || '';
  });
  const [displayLabel, setDisplayLabel] = useState(streamTitle || 'CNC Files');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const selected = sortedVersions.find((v) => v.id === selectedFileId);

  const handleRelease = async () => {
    if (!selected?.id) {
      setError('Select a file version.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await releaseProjectFileToVendor({
        rfqProjectId,
        supplierOrgId,
        dealId,
        projectFileId: selected.id,
        displayLabel: displayLabel.trim() || streamTitle,
      });
      onReleased?.(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Release failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pm-modal-overlay" role="presentation" onClick={() => !submitting && onClose()}>
      <div
        className="pm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="release-vendor-title"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        <div className="pm-modal-header">
          <h2 id="release-vendor-title">Release file to vendor</h2>
          <button type="button" className="pm-icon-btn" onClick={onClose} disabled={submitting} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="pm-modal-body">
          <p className="pm-muted" style={{ marginBottom: '0.75rem' }}>
            Creates an immutable copy for CNC vendors. Source: {streamTitle}
          </p>
          <label style={{ display: 'block', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Version</span>
            <select
              value={selectedFileId}
              onChange={(e) => setSelectedFileId(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: '0.25rem', padding: '0.4rem' }}
            >
              {sortedVersions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.versionIndex ?? '?'} — {v.name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'block', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Display label</span>
            <input
              type="text"
              value={displayLabel}
              onChange={(e) => setDisplayLabel(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: '0.25rem', padding: '0.4rem' }}
            />
          </label>
          {error && <p style={{ color: '#b45309', fontSize: '0.85rem' }}>{error}</p>}
        </div>
        <div className="pm-modal-footer">
          <button type="button" className="pm-btn secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="pm-btn primary" onClick={() => void handleRelease()} disabled={submitting}>
            {submitting ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
            Release
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseToVendorModal;
